import { bindingHelpers, buildAvailabilityWindow } from "./templateBindings";
import { formatPrice, isFeaturedItem, normalizeLookupKey } from "./templateNodes";
import { formatMoney } from "@/lib/money";
import {
  DEFAULT_TIMEZONE,
  buildSectionArtifacts,
  getZonedDateParts,
  isItemScheduleActive,
  pickCategoryCollectionId,
  readBool,
  readNum,
  readStr,
  slugifyMenuCollection,
} from "./templateMenuHelpers";
import type { Category, Item, MenuCollection as TemplateMenuCollection, MenuData } from "../types/template";
import { Branch, Business, MenuCollection as MenuCollectionRecord, currencies, Promotion } from "@/lib/types";
import { decoratePromotionForTemplate } from "@/lib/promotions/presentation";
import { isPromotionActive } from "@/lib/promotions/scheduler";

/**
 * `menu-images` (and every other public bucket) lives in the **Storage**
 * project, which is separate from the DB project (AGENTS.md §9). Public URLs
 * must therefore be built from `NEXT_PUBLIC_SUPABASE_STORAGE_URL`; using the
 * DB ref (`NEXT_PUBLIC_SUPABASE_URL`) pointed them at the wrong project.
 */
function storagePublicBaseUrl(): string | undefined {
  return process.env.NEXT_PUBLIC_SUPABASE_STORAGE_URL?.replace(/\/+$/, "");
}

function resolveMediaField(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (
    /^(https?:|data:|blob:)/i.test(trimmed) ||
    trimmed.startsWith("/_next/") ||
    trimmed.startsWith("/")
  ) {
    if (trimmed.includes("/storage/v1/object/public/") && !/^https?:/i.test(trimmed)) {
      const storageUrl = storagePublicBaseUrl();
      return storageUrl ? `${storageUrl}${trimmed.startsWith("/") ? "" : "/"}${trimmed}` : trimmed;
    }
    return trimmed;
  }

  const storageUrl = storagePublicBaseUrl();
  if (!storageUrl) return trimmed;

  const storagePath = trimmed
    .replace(/^\/+/, "")
    .replace(/^storage\/v1\/object\/public\//, "")
    .replace(/^menu-images\/+/, "");
  return `${storageUrl}/storage/v1/object/public/menu-images/${encodeURI(storagePath)}`;
}

export function resolveNormalizedItemImage(item: Record<string, unknown>): string | undefined {
  return (
    resolveMediaField(item?.image) ||
    resolveMediaField(item?.image_url) ||
    resolveMediaField(item?.imageUrl) ||
    resolveMediaField(item?.photo) ||
    resolveMediaField(item?.photo_url) ||
    resolveMediaField(item?.photoUrl) ||
    resolveMediaField(item?.thumbnail) ||
    resolveMediaField(item?.thumbnail_url) ||
    resolveMediaField(item?.thumbnailUrl) ||
    resolveMediaField(item?.preview_image) ||
    resolveMediaField(item?.previewImage)
  );
}

/**
 * A usable cut-out is only exposed once background removal has finished
 * (`image_cutout_status === "ready"`). Until then the field is omitted so the
 * template falls back to the original `image` (see doc 03 §3). The original
 * photo is never replaced — flagging T5.
 */
export function resolveNormalizedItemCutout(item: Record<string, unknown>): string | undefined {
  const status = typeof item?.image_cutout_status === "string" ? item.image_cutout_status.trim().toLowerCase() : "";
  if (status && status !== "ready") return undefined;
  return resolveMediaField(item?.image_cutout_url) || resolveMediaField(item?.imageCutoutUrl);
}

const NEW_ITEM_WINDOW_DAYS = 14;

function isWithinDays(value: unknown, days: number): boolean {
  if (typeof value !== "string" || !value.trim()) return false;
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return false;
  const ageMs = Date.now() - time;
  return ageMs >= 0 && ageMs <= days * 24 * 60 * 60 * 1000;
}

function tagTokens(value: unknown): Set<string> {
  const tokens = new Set<string>();
  const push = (entry: unknown) => {
    const key = normalizeLookupKey(entry);
    if (key) tokens.add(key);
  };
  if (Array.isArray(value)) value.forEach(push);
  else if (typeof value === "string") value.split(",").forEach(push);
  return tokens;
}

/**
 * Deterministic item badges, in stable display order: `new`, `spicy`,
 * `chef_pick`, `vegan` (doc 03 §3). Grounded in real item fields only.
 */
export function buildItemBadges(item: Record<string, unknown>, isFeatured: boolean): string[] {
  const tags = tagTokens(item?.tags);
  const dietary = tagTokens(item?.dietary_tags);

  const spiceRaw = item?.spice_level;
  const spice = typeof spiceRaw === "number" ? spiceRaw : Number(spiceRaw);
  const isSpicy = (Number.isFinite(spice) && spice >= 2) || tags.has("spicy") || tags.has("hot");

  const badges: string[] = [];
  if (item?.is_new === true || tags.has("new") || isWithinDays(item?.created_at ?? item?.createdAt, NEW_ITEM_WINDOW_DAYS)) {
    badges.push("new");
  }
  if (isSpicy) badges.push("spicy");
  if (isFeatured) badges.push("chef_pick");
  if (dietary.has("vegan") || tags.has("vegan") || dietary.has("plant based") || tags.has("plant based")) {
    badges.push("vegan");
  }
  return badges;
}

function toNumericPrice(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.\-]/g, "");
    if (!cleaned) return undefined;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function collectOptionPrices(item: Record<string, unknown>): number[] {
  const prices: number[] = [];
  const variants = item?.variants;
  if (variants && typeof variants === "object" && !Array.isArray(variants)) {
    for (const raw of Object.values(variants as Record<string, unknown>)) {
      const parsed = toNumericPrice(raw);
      if (parsed != null) prices.push(parsed);
    }
  }
  const groups = item?.option_groups;
  if (Array.isArray(groups)) {
    for (const group of groups) {
      const options = (group as Record<string, unknown>)?.options;
      if (!Array.isArray(options)) continue;
      for (const option of options) {
        const parsed = toNumericPrice((option as Record<string, unknown>)?.price);
        if (parsed != null) prices.push(parsed);
      }
    }
  }
  return prices;
}

/**
 * `range` price: min–max across an item's options — variant prices, option-group
 * prices, or weight-based pricing (doc 03 §3). Returns undefined when the item
 * has a single price, so callers fall back to the flat `price`.
 */
export function buildItemPriceRange(
  item: Record<string, unknown>,
  options: { currencyCode?: string; priceUnitLabel?: string | null } = {},
): string | undefined {
  const unique = [...new Set(collectOptionPrices(item).map((value) => Number(value.toFixed(2))))].sort((a, b) => a - b);
  let min: number | undefined;
  let max: number | undefined;
  if (unique.length >= 2) {
    min = unique[0];
    max = unique[unique.length - 1];
  } else {
    const rate = toNumericPrice(item?.price_per_unit_weight);
    const weightMin = toNumericPrice(item?.min_weight);
    const weightMax = toNumericPrice(item?.max_weight);
    if (rate != null && rate > 0 && weightMin != null && weightMax != null && weightMax > weightMin) {
      min = rate * weightMin;
      max = rate * weightMax;
    }
  }
  if (min == null || max == null || min === max) return undefined;
  const range = `${formatMoney(min, options.currencyCode)}–${formatMoney(max, options.currencyCode)}`;
  return options.priceUnitLabel ? `${range} ${options.priceUnitLabel}` : range;
}

/**
 * `dual` price: labelled sizes from an item's `variants` map, e.g.
 * `Regular ₵25.00 · Family ₵45.00` (doc 03 §3). Requires ≥2 numeric variants.
 */
export function buildItemPriceDual(
  item: Record<string, unknown>,
  options: { currencyCode?: string } = {},
): string | undefined {
  const variants = item?.variants;
  if (!variants || typeof variants !== "object" || Array.isArray(variants)) return undefined;
  const entries = Object.entries(variants as Record<string, unknown>)
    .map(([label, raw]) => {
      const cleanLabel = String(label ?? "").trim();
      const price = toNumericPrice(raw);
      return cleanLabel && price != null ? `${cleanLabel} ${formatMoney(price, options.currencyCode)}` : null;
    })
    .filter((entry): entry is string => Boolean(entry));
  if (entries.length < 2) return undefined;
  return entries.join(" · ");
}

/**
 * Group an integer with plain commas (never locale-dependent — the same reason
 * lib/money.ts avoids toLocaleString).
 */
function groupThousands(value: number): string {
  const rounded = Math.round(value);
  return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * `dash` price (doc 03a "New price formats"): the number with no currency
 * symbol and a ".-" suffix, e.g. `29,000.-`.
 */
export function buildItemPriceDash(value: number | null | undefined): string | undefined {
  const n = toNumericPrice(value);
  if (n == null) return undefined;
  return `${groupThousands(n)}.-`;
}

/**
 * `code` price: the ISO code printed before the number, e.g. `GHS 29,000`
 * (falls back to the symbol formatter when no code is known).
 */
export function buildItemPriceCode(
  value: number | null | undefined,
  currencyCode?: string,
): string | undefined {
  const n = toNumericPrice(value);
  if (n == null) return undefined;
  const code = typeof currencyCode === "string" && currencyCode.trim() ? currencyCode.trim() : "";
  return code ? `${code} ${groupThousands(n)}` : formatMoney(n, currencyCode);
}

/**
 * `short` price: K/M shorthand for large amounts (`29000` → `29K`), otherwise
 * the plain grouped number.
 */
export function buildItemPriceShort(value: number | null | undefined): string | undefined {
  const n = toNumericPrice(value);
  if (n == null) return undefined;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${Math.round(n / 1_000)}K`;
  return groupThousands(n);
}

export function resolveNormalizedCategoryImage(category: Record<string, unknown>): string | undefined {
  return (
    resolveMediaField(category?.image) ||
    resolveMediaField(category?.image_url) ||
    resolveMediaField(category?.imageUrl) ||
    resolveMediaField(category?.photo) ||
    resolveMediaField(category?.photo_url) ||
    resolveMediaField(category?.photoUrl) ||
    resolveMediaField(category?.thumbnail) ||
    resolveMediaField(category?.thumbnail_url) ||
    resolveMediaField(category?.thumbnailUrl) ||
    resolveMediaField(category?.preview_image) ||
    resolveMediaField(category?.previewImage)
  );
}

export function buildMenuData({
  business,
  menu: menuRaw,
  categories,
  itemsByCategory,
  promotions,
  branch,
  collections,
}: {
  business: Business | null | undefined;
  menu: Record<string, unknown>;
  categories: Record<string, unknown>[];
  itemsByCategory: Record<string, unknown[]>;
  promotions: Promotion[];
  branch?: Branch | null;
  collections?: MenuCollectionRecord[];
}): MenuData {
  const menu = menuRaw as Record<string, unknown> & {
    id?: string; title?: string; slug?: string; description?: string;
    branch_id?: string; currency?: string; template_id?: string;
    branch?: Record<string, unknown>; branches?: Record<string, unknown>[];
    slider?: boolean;
  };

  const resolvedBranch: Record<string, unknown> | null =
    branch as Record<string, unknown> | null ||
    (menu?.branch as Record<string, unknown> | undefined) ||
    (Array.isArray(menu?.branches) ? (menu?.branches as Record<string, unknown>[])[0] : null) ||
    (Array.isArray(business?.branches)
      ? (business?.branches as unknown as Record<string, unknown>[]).find((entry) => entry?.id === menu?.branch_id) || null
      : null);
  const scheduleTimezone = readStr(resolvedBranch?.timezone) || DEFAULT_TIMEZONE;
  const branchMetadata =
    resolvedBranch?.metadata && typeof resolvedBranch.metadata === 'object'
      ? (resolvedBranch.metadata as Record<string, unknown>)
      : {};
  const resolveBranchMetaString = (...keys: string[]) => {
    for (const key of keys) {
      const value = branchMetadata?.[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return undefined;
  };

  const effectiveCurrency = readStr(menu?.currency) || readStr(business?.currency) || "USD";
  const currrencySymbol = currencies.find((c) => c.code === effectiveCurrency)?.symbol || "$";
  const activePromotions = (promotions || [])
    .filter((promotion) => Boolean(promotion) && isPromotionActive(promotion))
    .map((promotion) => {
      const decorated = decoratePromotionForTemplate(promotion, { currencySymbol: currrencySymbol });
      // Legacy templates reference {promo.custom_label} expecting the full computed
      // promo label (e.g. "+20%", "Buy One Get One"). Wire promoLabel into custom_label
      // so those bindings resolve correctly without updating every legacy template.
      const computedLabel = bindingHelpers.promoLabel(decorated, { currency: currrencySymbol });
      return { ...decorated, custom_label: computedLabel || decorated.custom_label || "" };
    });
  const isMenuScopedPromotion = (promotion: Promotion) =>
    promotion.target_type === 'menu' &&
    (
      (Array.isArray(promotion.target_ids) && typeof promotion.target_ids[0] === 'string' && promotion.target_ids.includes(String(menu?.id || ''))) ||
      (promotion.menu_id && String(promotion.menu_id) === String(menu?.id || ''))
    );
  const isBusinessScopedPromotion = (promotion: Promotion) =>
    promotion.target_type === 'business' &&
    String(promotion.business_id || '') === String(business?.id || '');
  const resolvedAddress = resolveBranchMetaString('address') || readStr(resolvedBranch?.address) || readStr(business?.address) || "";
  const resolvedPhone = resolveBranchMetaString('phone') || readStr(resolvedBranch?.phone) || readStr(business?.phone) || "";
  const resolvedEmail = resolveBranchMetaString('email') || readStr(resolvedBranch?.email) || readStr(business?.email) || "";
  const resolvedWebsite = resolveBranchMetaString('website') || readStr(business?.website) || "";
  const resolvedMapsUrl = resolveBranchMetaString('google_maps_url', 'maps_url') || readStr(business?.google_maps_url) || "";
  const resolvedBranchName = readStr(resolvedBranch?.name) || readStr(business?.name) || readStr(menu?.title) || 'Menu';
  const resolvedParentName = readStr(business?.name) || readStr(resolvedBranch?.name) || readStr(menu?.title) || 'Menu';
  const resolvedTagline = resolveBranchMetaString('tagline', 'strapline') || readStr(business?.tagline) || "";
  const resolvedBranchDescription =
    resolveBranchMetaString('description', 'summary') ||
    readStr(menu?.description) ||
    readStr(business?.description) ||
    "";
  const resolvedLocation =
    resolveBranchMetaString('location') ||
    readStr(resolvedBranch?.address) ||
    readStr(business?.location) ||
    readStr(business?.address) ||
    "";

  const providedCollections = Array.isArray(collections) ? collections : [];
  const fallbackCollectionId =
    providedCollections.find((entry) => entry?.is_default)?.id ||
    `${menu?.id || menu?.slug || 'menu'}-default`;

  const collectionRegistry = new Map<string, MenuCollectionRecord>();
  const registerCollection = (collection: Partial<MenuCollectionRecord>, orderHint: number) => {
    const id = String(collection.id || '').trim();
    if (!id) return;
    const key = normalizeLookupKey(id);
    if (!key) return;

    const current = collectionRegistry.get(key);
    collectionRegistry.set(key, {
      id,
      menu_id: String(collection.menu_id || menu?.id || ''),
      name: String(collection.name || current?.name || 'Main Menu'),
      slug: String(collection.slug || current?.slug || slugifyMenuCollection(collection.name || current?.name || 'Main Menu')),
      description: collection.description ?? current?.description,
      icon: collection.icon ?? current?.icon,
      sort_order: Number(collection.sort_order ?? current?.sort_order ?? orderHint),
      is_default: Boolean(collection.is_default ?? current?.is_default ?? false),
      is_active: Boolean(collection.is_active ?? current?.is_active ?? true),
      created_at: collection.created_at ?? current?.created_at,
      updated_at: collection.updated_at ?? current?.updated_at,
    });
  };

  providedCollections.forEach((collection, idx) => registerCollection(collection, idx));

  categories.forEach((category, idx) => {
    const collectionId = pickCategoryCollectionId(category, fallbackCollectionId);
    registerCollection(
      {
        id: collectionId,
        menu_id: readStr(menu?.id),
        name:
          readStr(category?.collection_name) ||
          readStr(category?.collectionName) ||
          (normalizeLookupKey(collectionId) === normalizeLookupKey(fallbackCollectionId)
            ? 'Main Menu'
            : String(collectionId).replace(/[_-]+/g, ' ')),
        slug:
          readStr(category?.collection_slug) ||
          readStr(category?.collectionSlug) ||
          slugifyMenuCollection(
            readStr(category?.collection_name) ||
              readStr(category?.collectionName) ||
              (normalizeLookupKey(collectionId) === normalizeLookupKey(fallbackCollectionId)
                ? 'Main Menu'
                : String(collectionId)),
          ),
        sort_order: Number(category?.collection_order ?? category?.collectionOrder ?? idx),
        is_default: normalizeLookupKey(collectionId) === normalizeLookupKey(fallbackCollectionId),
      },
      idx,
    );
  });

  if (collectionRegistry.size === 0) {
    registerCollection(
      {
        id: fallbackCollectionId,
        menu_id: readStr(menu?.id),
        name: 'Main Menu',
        slug: 'main-menu',
        sort_order: 0,
        is_default: true,
        is_active: true,
      },
      0,
    );
  }

  const normalizedCollections = Array.from(collectionRegistry.values()).sort(
    (a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0),
  );
  const collectionByKey = new Map(normalizedCollections.map((collection) => [normalizeLookupKey(collection.id), collection]));
  const defaultCollection =
    normalizedCollections.find((entry) => entry.is_default) ||
    normalizedCollections[0];

  const menuCategories = categories.map((cat, idx) => {
    const collectionId = pickCategoryCollectionId(cat, defaultCollection?.id || fallbackCollectionId);
    const collectionMeta =
      collectionByKey.get(normalizeLookupKey(collectionId)) ||
      defaultCollection;
    const sectionId =
      cat.section_id ||
      cat.sectionId ||
      cat.section ||
      cat.group_id ||
      cat.groupId ||
      undefined;
    const sectionLabel =
      cat.section_label ||
      cat.sectionLabel ||
      cat.section_name ||
      cat.sectionName ||
      (sectionId ? String(sectionId).replace(/[_-]+/g, " ") : undefined);
    const sectionOrder = cat.section_order || cat.sectionOrder || undefined;

    const mappedItems = (cat.id ? (itemsByCategory[String(cat.id)] || []) : [])
      .map((item) => {
        const i = item as Record<string, unknown>;
        const baseAvailable =
          typeof i.available === "boolean"
            ? i.available
            : typeof i.is_available === "boolean"
              ? i.is_available
              : true;
        const scheduleActive = isItemScheduleActive(i, scheduleTimezone);
        const sellState = typeof i.sell_state === "string" && i.sell_state.trim()
          ? i.sell_state.trim()
          : "available";
        const comingSoon = Boolean(
          i.is_coming_soon ||
            i.coming_soon ||
            (i.available_from && String(i.available_from) > getZonedDateParts(new Date(), scheduleTimezone).dateKey),
        );
        const visibleState = comingSoon || sellState === "sold_out" || sellState === "paused";

        if ((!baseAvailable || !scheduleActive) && !visibleState) {
          return null;
        }

        const itemKind = typeof i.kind === "string" && i.kind ? i.kind : "dish";
        const pricingMode = typeof i.pricing_mode === "string" ? i.pricing_mode : "flat";
        const typeProfile =
          i.type_profile && typeof i.type_profile === "object"
            ? (i.type_profile as Record<string, unknown>)
            : {};
        const priceUnitLabel =
          pricingMode === "per_person" || itemKind === "package"
            ? "per person"
            : itemKind === "service" && typeof typeProfile.unit_label === "string" && typeProfile.unit_label
              ? `per ${typeProfile.unit_label}`
              : null;
        const packageFacts: string[] = [];
        if (itemKind === "package") {
          const minHeadcount = Number(typeProfile.min_headcount);
          const leadTimeDays = Number(typeProfile.lead_time_days);
          const depositPct = Number(typeProfile.deposit_pct);
          if (Number.isFinite(minHeadcount) && minHeadcount > 0) packageFacts.push(`Min ${minHeadcount} guests`);
          if (Number.isFinite(leadTimeDays) && leadTimeDays > 0) {
            packageFacts.push(`${leadTimeDays}-day notice`);
          }
          if (Number.isFinite(depositPct) && depositPct > 0) packageFacts.push(`${depositPct}% deposit`);
          if (typeof typeProfile.serves_note === "string" && typeProfile.serves_note.trim()) {
            packageFacts.push(typeProfile.serves_note.trim());
          }
          const tiers = Array.isArray(typeProfile.tiers) ? (typeProfile.tiers as Record<string, unknown>[]) : [];
          const tierLine = tiers
            .filter((tier) => tier && typeof tier.name === "string" && Number.isFinite(Number(tier.price_per_person)))
            .map((tier) => `${tier.name} ${currrencySymbol}${formatPrice(Number(tier.price_per_person))}`)
            .join(" · ");
          if (tierLine) packageFacts.push(`Tiers: ${tierLine} per person`);
        }
        const descriptionWithFacts = [
          typeof i.description === "string" ? i.description.trim() : "",
          packageFacts.join(" · "),
        ]
          .filter(Boolean)
          .join(" , ");
        const rawPrice = readNum(i.price) ?? Number(i.price || 0);
        const featured = isFeaturedItem(i);

        return {
          id: readStr(i.id) || '',
          name: readStr(i.name) || readStr(i.title) || '',
          title: readStr(i.title) || readStr(i.name) || '',
          description: descriptionWithFacts || readStr(i.description),
          currency: currrencySymbol,
          price: priceUnitLabel ? `${formatPrice(rawPrice)} ${priceUnitLabel}` : formatPrice(rawPrice),
          price_value: rawPrice,
          kind: itemKind,
          pricing_mode: pricingMode,
          price_per_unit_weight: i.price_per_unit_weight != null ? Number(i.price_per_unit_weight) : null,
          weight_unit: i.weight_unit != null ? String(i.weight_unit) : null,
          min_weight: i.min_weight != null ? Number(i.min_weight) : null,
          max_weight: i.max_weight != null ? Number(i.max_weight) : null,
          type_profile: i.type_profile ?? null,
          price_unit_label: priceUnitLabel,
          price_range: buildItemPriceRange(i, { currencyCode: effectiveCurrency, priceUnitLabel }),
          price_dual: buildItemPriceDual(i, { currencyCode: effectiveCurrency }),
          price_dash: buildItemPriceDash(rawPrice),
          price_code: buildItemPriceCode(rawPrice, effectiveCurrency),
          price_short: buildItemPriceShort(rawPrice),
          image: resolveNormalizedItemImage(i),
          image_cutout_url: resolveNormalizedItemCutout(i),
          badges: buildItemBadges(i, featured),
          tags: i.tags,
          available: baseAvailable && scheduleActive,
          available_Days: i.available_Days,
          available_Times: i.available_Times,
          available_from: i.available_from ?? null,
          available_until: i.available_until ?? null,
          availability_label: readStr(i.availability_label) || buildAvailabilityWindow(i),
          sell_state: sellState,
          sell_state_reason: i.sell_state_reason ?? null,
          available_for_order:
            typeof i.available_for_order === "boolean"
              ? i.available_for_order
              : baseAvailable && scheduleActive && sellState === "available" && !comingSoon,
          display_order: readNum(i.display_order) ?? 0,
          allergens: i.allergens,
          dietary_tags: i.dietary_tags,
          nutritional_info: i.nutritional_info,
          variants: i.variants,
          extras: i.extras,
          option_groups: Array.isArray(i.option_groups) ? i.option_groups : [],
          is_featured: featured,
          is_coming_soon: comingSoon,
          coming_soon: comingSoon,
          created_at: i.created_at || i.createdAt,
          category_id: cat.id,
          category_name: cat.name,
          collection_id: collectionMeta?.id,
          collection_name: collectionMeta?.name,
          collection_slug: collectionMeta?.slug,
          section_id: sectionId,
          promotion: (() => {
            
            const promos = activePromotions.filter(
              (promo) =>
                (
                  (promo.target_type === "item" &&
                    Array.isArray(promo.target_ids) &&
                    promo.target_ids.includes(readStr(i.id) || "")) ||
                  isMenuScopedPromotion(promo) ||
                  isBusinessScopedPromotion(promo)
                ),
            );
            return promos.length > 0 ? promos[0] : undefined;
          })(),
        };
      })
      .filter(Boolean) as Item[];

    mappedItems.forEach((item, itemIndex) => {
      item.index = itemIndex + 1;
    });

    return {
      id: readStr(cat.id) || '',
      name: readStr(cat.name) || '',
      description: readStr(cat.description),
      image: resolveNormalizedCategoryImage(cat),
      display_order: readNum(cat.display_order) ?? 0,
      collection_id: collectionMeta?.id,
      collection_name: collectionMeta?.name,
      collection_slug: collectionMeta?.slug,
      collection_order: Number(collectionMeta?.sort_order ?? idx),
      section_id: sectionId,
      section_label: sectionLabel,
      section_order: sectionOrder,
      items: mappedItems,
      featured_items: mappedItems.filter((item) => isFeaturedItem(item)),
      promotion: (() => {
        const promos = activePromotions.filter(
          (promo) =>
            (
              (promo.target_type === "category" &&
                Array.isArray(promo.target_ids) &&
                promo.target_ids.includes(readStr(cat.id) || "")) ||
              isMenuScopedPromotion(promo) ||
              isBusinessScopedPromotion(promo)
            ),
        );
        return promos.length > 0 ? promos[0] : undefined;
      })(),
    };
  }) as Category[];

  const allItems = menuCategories.flatMap((cat) => cat.items || []);
  const featuredItems = allItems.filter((item) => isFeaturedItem(item));
  const { sections, categoriesBySection } = buildSectionArtifacts(menuCategories);

  const collectionViews: TemplateMenuCollection[] = normalizedCollections
    .map((collection) => {
      const collectionCategories = menuCategories.filter(
        (category) => normalizeLookupKey(category.collection_id) === normalizeLookupKey(collection.id),
      );
      const collectionItems = collectionCategories.flatMap((category) => category.items || []);
      const collectionFeaturedItems = collectionItems.filter((item) => isFeaturedItem(item));
      const collectionPromotions = activePromotions.filter((promotion) => {
        const targetIds = Array.isArray(promotion.target_ids) ? promotion.target_ids : [];

        if (promotion.target_type === 'collection') {
          return targetIds.includes(collection.id || '');
        }

        if (promotion.target_type === 'category') {
          return collectionCategories.some((category) => targetIds.includes(category.id || ''));
        }

        if (promotion.target_type === 'item') {
          return collectionItems.some((item) => targetIds.includes(item.id || ''));
        }

        if (isMenuScopedPromotion(promotion)) {
          return true;
        }

        if (isBusinessScopedPromotion(promotion)) {
          return true;
        }

        return false;
      });
      const collectionSectionArtifacts = buildSectionArtifacts(collectionCategories);

      return {
        id: collection.id,
        name: collection.name,
        slug: collection.slug,
        description: collection.description,
        icon: collection.icon,
        sort_order: collection.sort_order,
        is_default: collection.is_default,
        categories: collectionCategories,
        items: collectionItems,
        featuredItems: collectionFeaturedItems,
        promotions: collectionPromotions,
        sections: collectionSectionArtifacts.sections,
        categoriesBySection: collectionSectionArtifacts.categoriesBySection,
      };
    })
    .filter((collection) => collection.categories.length > 0 || collection.is_default);

  return {
    business: business
      ? {
          name: resolvedBranchName,
          parentName: resolvedParentName,
          slug: readStr(resolvedBranch?.slug) || readStr((business as Record<string, unknown>)?.slug) || '',
          description: readStr((business as Record<string, unknown>).description) || readStr(menu?.description) || "",
          branchName: readStr(resolvedBranch?.name) || undefined,
          branchSlug: readStr(resolvedBranch?.slug) || undefined,
          branchDescription: resolvedBranchDescription || undefined,
          logo: readStr((business as Record<string, unknown>).logo_url) || "",
          brandColor: readStr((business as Record<string, unknown>).theme_id),
          phone: resolvedPhone,
          website: resolvedWebsite,
          tagline: resolvedTagline,
          address: resolvedAddress,
          currency: effectiveCurrency,
          email: resolvedEmail,
          location: resolvedLocation,
          mapsUrl: resolvedMapsUrl,

          socialLinks: [
            ...(readStr((business as Record<string, unknown>).instagram) ? [{ platform: "instagram", url: readStr((business as Record<string, unknown>).instagram) || "" }] : []),
            ...(readStr((business as Record<string, unknown>).twitter) ? [{ platform: "twitter", url: readStr((business as Record<string, unknown>).twitter) || "" }] : []),
            ...(resolvedWebsite ? [{ platform: "website", url: resolvedWebsite }] : []),
          ],
        }
      : undefined,
    menu: {
      id: readStr(menu?.id),
      title: readStr(menu?.title) || '',
      description: readStr(menu?.description),
      slug: readStr(menu?.slug) || '',
      template_id: readStr(menu?.template_id),
      branch_id: readStr(resolvedBranch?.id) || readStr(menu?.branch_id) || null,
      branch_name: readStr(resolvedBranch?.name) || undefined,
      collections: collectionViews,
      categories: menuCategories,
      items: allItems,
      featuredItems,
      sections,
      categoriesBySection,
    },
    items: allItems,
    featuredItems,
    slider: readBool(menu?.slider) ?? false,
  };
}
